import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { 
  CreditCard, 
  Globe, 
  Shield, 
  BarChart3, 
  Zap, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Smartphone,
  Building,
  Banknote,
  Lock,
  TrendingUp,
  Settings,
  Code,
  Star,
  Languages,
  ExternalLink,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX
} from 'lucide-react';
import mockData from '../mock';
import ContactFormModal from './ContactFormModal';
import { getInitialLanguage, applyLanguage } from '../lib/language';

const LandingPage = ({ initialLanguage }) => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [language, setLanguage] = useState(() => initialLanguage || getInitialLanguage());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [heroVideoMuted, setHeroVideoMuted] = useState(true);
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  const heroVideoRef = React.useRef(null);

  // Show static portal screenshot for 5 seconds, then swap to the video.
  // The user can also click the screenshot to start it earlier.
  React.useEffect(() => {
    const timer = setTimeout(() => setShowHeroVideo(true), 5000);
    return () => clearTimeout(timer);
  }, []);
  
  React.useEffect(() => {
    setLanguage(initialLanguage || getInitialLanguage());
  }, [initialLanguage]);

  React.useEffect(() => {
    applyLanguage(language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const t = mockData.languages[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div 
              className="flex items-center space-x-3 cursor-pointer"
              onClick={handleLogoClick}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_3de5042f-38bc-4c59-be9d-105ed5dfd1b8/artifacts/mfv2x0ox_paymentpower%20azul.webp" 
                alt="PaymentPower"
                className="h-12 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-slate-600 hover:text-blue-600 transition-colors">{t.nav.features}</a>
              <a href="#services" className="text-slate-600 hover:text-blue-600 transition-colors">{t.nav.services}</a>
              <a href="#clients" className="text-slate-600 hover:text-blue-600 transition-colors">
                {language === 'en' ? 'Clients' : 'Clientes'}
              </a>
              <a href="#audience" className="text-slate-600 hover:text-blue-600 transition-colors">
                {language === 'en' ? 'Audience' : 'Audiencia'}
              </a>
              <a href="#pricing" className="text-slate-600 hover:text-blue-600 transition-colors">{t.nav.pricing}</a>
            </nav>
            
            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-600 hover:text-blue-600 flex items-center space-x-2 px-3"
                title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
              >
                <img 
                  src={language === 'en' 
                    ? 'https://flagcdn.com/w40/es.png' 
                    : 'https://flagcdn.com/w40/us.png'
                  } 
                  alt={language === 'en' ? 'Español' : 'English'}
                  className="w-6 h-4 object-cover rounded-sm"
                />
                <span>{language === 'en' ? 'ES' : 'EN'}</span>
              </Button>
              <Button 
                variant="ghost" 
                className="text-slate-600 hover:text-blue-600"
                onClick={() => window.open('https://portal.paymentpower.net/', '_blank')}
                data-testid="sign-in-btn"
              >
                {t.nav.signIn}
              </Button>
              <Button 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6"
                onClick={() => setContactModalOpen(true)}
                data-testid="get-started-btn"
              >
                {t.nav.getStarted}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-600 hover:text-blue-600 flex items-center space-x-1 px-2"
                title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
              >
                <img 
                  src={language === 'en' 
                    ? 'https://flagcdn.com/w40/es.png' 
                    : 'https://flagcdn.com/w40/us.png'
                  } 
                  alt={language === 'en' ? 'Español' : 'English'}
                  className="w-5 h-3.5 object-cover rounded-sm"
                />
                <span className="text-xs">{language === 'en' ? 'ES' : 'EN'}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMobileMenu}
                className="text-slate-600 hover:text-blue-600"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-lg">
              <div className="px-4 py-3 space-y-3">
                <a 
                  href="#features" 
                  className="block py-2 text-slate-600 hover:text-blue-600 transition-colors"
                  onClick={closeMobileMenu}
                >
                  {t.nav.features}
                </a>
                <a 
                  href="#services" 
                  className="block py-2 text-slate-600 hover:text-blue-600 transition-colors"
                  onClick={closeMobileMenu}
                >
                  {t.nav.services}
                </a>
                <a 
                  href="#clients" 
                  className="block py-2 text-slate-600 hover:text-blue-600 transition-colors"
                  onClick={closeMobileMenu}
                >
                  {language === 'en' ? 'Clients' : 'Clientes'}
                </a>
                <a 
                  href="#audience" 
                  className="block py-2 text-slate-600 hover:text-blue-600 transition-colors"
                  onClick={closeMobileMenu}
                >
                  {language === 'en' ? 'Audience' : 'Audiencia'}
                </a>
                <a 
                  href="#pricing" 
                  className="block py-2 text-slate-600 hover:text-blue-600 transition-colors"
                  onClick={closeMobileMenu}
                >
                  {t.nav.pricing}
                </a>
                <div className="pt-3 border-t border-slate-200/60 space-y-2">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start text-slate-600 hover:text-blue-600"
                    onClick={() => {
                      closeMobileMenu();
                      window.open('https://portal.paymentpower.net/', '_blank');
                    }}
                    data-testid="mobile-sign-in-btn"
                  >
                    {t.nav.signIn}
                  </Button>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                    onClick={() => {
                      closeMobileMenu();
                      setContactModalOpen(true);
                    }}
                    data-testid="mobile-get-started-btn"
                  >
                    {t.nav.getStarted}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.1)_0%,transparent_65%)]"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* LEFT — Text content */}
            <div className="text-center lg:text-left" data-testid="hero-text-content">
              <div className="mb-6 inline-block bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium border border-white/20">
                {t.hero.badge}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight">
                {t.hero.title}
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent block">
                  {t.hero.titleHighlight}
                </span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-200 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t.hero.subtitle}
              </p>

              <div className="max-w-xl mx-auto lg:mx-0">
                <p className="text-base lg:text-lg font-semibold text-white mb-2">
                  {t.hero.multiPaymentLine1}
                </p>
                <p className="text-sm lg:text-base text-slate-300">
                  {t.hero.multiPaymentLine2}
                </p>
              </div>
            </div>

            {/* RIGHT — Merchant Portal screenshot */}
            <div className="relative" data-testid="hero-portal-screen">
              <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-purple-500/20 blur-3xl transform scale-110"></div>

              {/* Floating Explore Demo CTA — top-right above portal screen */}
              <div className="flex justify-end mb-4 lg:mb-6 relative z-20">
                <Button
                  size="lg"
                  data-testid="hero-explore-demo-btn"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all"
                  onClick={() => {
                    window.open('https://portal.paymentpower.net/', '_blank');
                  }}
                >
                  {t.hero.watchDemo}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>

              {/* Glowing stars decoration */}
              <div className="absolute -top-4 -left-4 w-8 h-8 text-yellow-300/60 z-10">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div className="absolute -bottom-6 -right-6 w-12 h-12 text-purple-300/40 z-10">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>

              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10 backdrop-blur-sm transform hover:scale-[1.02] transition-transform duration-500">
                {showHeroVideo ? (
                  <video
                    ref={heroVideoRef}
                    src="/portal-demo.mp4#t=10"
                    autoPlay
                    muted
                    playsInline
                    preload="auto"
                    poster="/portal-screen.png"
                    className="w-full h-auto block"
                    aria-label={language === 'en'
                      ? 'PaymentPower merchant portal walkthrough video'
                      : 'Video del recorrido por el portal de comerciante de PaymentPower'}
                    onLoadedMetadata={(e) => {
                      try {
                        e.currentTarget.currentTime = 10;
                        e.currentTarget.play().catch(() => {});
                      } catch (_) {}
                    }}
                    onEnded={(e) => {
                      e.currentTarget.currentTime = 10;
                      e.currentTarget.play().catch(() => {});
                    }}
                    data-testid="hero-portal-video"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowHeroVideo(true)}
                    className="block w-full text-left relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    aria-label={language === 'en' ? 'Play portal walkthrough video' : 'Reproducir video del portal'}
                    data-testid="hero-portal-play-poster"
                  >
                    <img
                      src="/portal-screen.png"
                      alt="PaymentPower merchant portal dashboard showing revenue, approved transactions, and order success metrics"
                      className="w-full h-auto block"
                    />
                    {/* Play badge overlay */}
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 shadow-2xl transition-transform duration-300 group-hover:scale-110">
                        <svg
                          className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 translate-x-0.5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </span>
                  </button>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent pointer-events-none"></div>

                {/* Sound toggle — only while video is showing */}
                {showHeroVideo && (
                  <button
                    type="button"
                    data-testid="hero-video-sound-toggle"
                    aria-label={
                      heroVideoMuted
                        ? (language === 'en' ? 'Unmute video' : 'Activar sonido')
                        : (language === 'en' ? 'Mute video' : 'Silenciar video')
                    }
                    onClick={() => {
                      const v = heroVideoRef.current;
                      if (!v) return;
                      const newMuted = !heroVideoMuted;
                      v.muted = newMuted;
                      setHeroVideoMuted(newMuted);
                      v.play().catch(() => {});
                    }}
                    className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-2 bg-slate-900/70 hover:bg-slate-900/90 backdrop-blur-sm text-white text-sm font-medium px-3 py-2 rounded-full border border-white/20 shadow-lg transition-all"
                  >
                    {heroVideoMuted ? (
                      <>
                        <VolumeX className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {language === 'en' ? 'Tap for sound' : 'Toca para audio'}
                        </span>
                      </>
                    ) : (
                      <Volume2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>

              {/* Additional glow effects */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 rounded-3xl blur-2xl -z-10"></div>
            </div>
          </div>
        </div>
        
        {/* Subtle bottom border to merge into next blue section */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5"></div>
      </section>

      {/* Portal Preview Section — Transactions + Analytics */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-indigo-900 via-blue-900 to-slate-900" data-testid="portal-preview-section">
        {/* Decorative glows */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5">
              {language === 'en' ? 'Inside the Portal' : 'Dentro del Portal'}
            </h2>
            <p className="text-lg md:text-xl text-blue-100/90 max-w-3xl mx-auto leading-relaxed">
              {language === 'en'
                ? 'A real preview of the operations console — transaction history, smart analytics, and approval rate insights, all under your brand.'
                : 'Una vista previa real de la consola de operaciones — historial de transacciones, analítica inteligente y métricas de tasa de aprobación, todo bajo tu marca.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Transactions History */}
            <div className="group" data-testid="portal-transactions-card">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:shadow-blue-500/20 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                <div className="absolute top-4 left-4 z-10">
                  <span className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
                    <CreditCard className="w-3.5 h-3.5" />
                    {language === 'en' ? 'Transactions History' : 'Historial de Transacciones'}
                  </span>
                </div>
                <img
                  src="/portal-transactions.png"
                  alt={language === 'en'
                    ? 'PaymentPower portal transactions history view with filterable list of payments by date, status, channel, merchant and currency'
                    : 'Vista de historial de transacciones del portal PaymentPower con lista filtrable de pagos por fecha, estado, canal, comerciante y moneda'}
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-6 px-2">
                <h3 className="text-xl font-bold text-white mb-2">
                  {language === 'en' ? 'Every Payment, One View' : 'Todos los Pagos, Una Vista'}
                </h3>
                <p className="text-blue-100/80 leading-relaxed">
                  {language === 'en'
                    ? 'Search, filter and export transactions across every merchant, channel and currency. Live status badges, 3DS results, customer details and payment-method icons in a single grid.'
                    : 'Busca, filtra y exporta transacciones de todos los comerciantes, canales y monedas. Estados en vivo, resultados 3DS, detalles del cliente e iconos de método de pago en una sola grilla.'}
                </p>
              </div>
            </div>

            {/* Analytics — Approval Rate */}
            <div className="group" data-testid="portal-analytics-card">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:shadow-purple-500/20 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                <div className="absolute top-4 left-4 z-10">
                  <span className="inline-flex items-center gap-2 bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
                    <TrendingUp className="w-3.5 h-3.5" />
                    {language === 'en' ? 'Analytics · Approval Rate' : 'Analítica · Tasa de Aprobación'}
                  </span>
                </div>
                <img
                  src="/portal-analytics.png"
                  alt={language === 'en'
                    ? 'PaymentPower analytics dashboard showing approval rate KPIs, payment funnel from total transactions to approved, and order success rate'
                    : 'Panel de analítica de PaymentPower que muestra KPIs de tasa de aprobación, embudo de pago de transacciones totales a aprobadas y tasa de éxito de órdenes'}
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-6 px-2">
                <h3 className="text-xl font-bold text-white mb-2">
                  {language === 'en' ? 'Lift Your Approval Rate' : 'Aumenta Tu Tasa de Aprobación'}
                </h3>
                <p className="text-blue-100/80 leading-relaxed">
                  {language === 'en'
                    ? 'Pinpoint where revenue leaks — risk checks, 3DS, processor declines — through a visual payment funnel and real-time approval-rate KPIs for every merchant and processor.'
                    : 'Identifica dónde se pierde revenue — controles de riesgo, 3DS, rechazos del procesador — con un embudo de pago visual y KPIs de tasa de aprobación en tiempo real por comerciante y procesador.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gradient fade into next white section */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {t.sections.featuresTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.sections.featuresSubtitle}
            </p>
          </div>
          
          {/* White-Label Branding Hero Section */}
          <div 
            className="mb-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 md:p-12 border-2 border-blue-200/50 shadow-xl"
            data-testid="white-label-hero-section"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  <Building className="w-4 h-4" />
                  <span>{language === 'en' ? 'Featured' : 'Destacado'}</span>
                </div>
                <h3 className="text-4xl font-bold text-slate-900">
                  {mockData.mainFeatures[language][0].title}
                </h3>
                <p className="text-xl text-slate-700 leading-relaxed">
                  {mockData.mainFeatures[language][0].details}
                </p>
                <div className="flex flex-wrap gap-3">
                  {mockData.mainFeatures[language][0].technologies.map((tech, i) => (
                    <Badge key={i} className="bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                  <img 
                    src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw2fHx3ZWIlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzYyMDE4NzQ1fDA&ixlib=rb-4.1.0&q=85&w=800"
                    alt="White-label payment dashboard with customizable branding, showing multiple UI themes and design variations for payment gateway customization"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-slate-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Building className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {language === 'en' ? '100% Customizable' : '100% Personalizable'}
                      </div>
                      <div className="text-xs text-slate-600">
                        {language === 'en' ? 'Your Brand, Your Way' : 'Tu Marca, Tu Manera'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4 Main Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {mockData.mainFeatures[language].slice(1, 5).map((feature, index) => (
              <Card 
                key={index}
                className="p-6 hover:shadow-xl transition-all duration-300 border-2 border-slate-200 hover:border-blue-500 bg-white group"
                data-testid={`main-feature-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="mb-6">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl inline-block group-hover:scale-110 transition-transform">
                      <feature.icon className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{feature.details}</p>
                  <div className="flex flex-wrap gap-2">
                    {feature.technologies.map((tech, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-slate-300 text-slate-700">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.additionalFeatures[language].map((feature, index) => (
              <Card 
                key={index} 
                className="p-6 hover:shadow-lg transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm"
                data-testid={`additional-feature-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl mb-4 inline-block">
                    <feature.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 mb-4">{feature.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {feature.tags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-blue-200 text-blue-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-24 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {t.sections.clientsTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.sections.clientsSubtitle}
            </p>
          </div>
          
          <div className="relative">
            {/* Mobile edge fade — hints at off-screen content */}
            <div className="md:hidden pointer-events-none absolute top-0 bottom-0 right-0 w-12 bg-gradient-to-l from-white/95 to-transparent z-10"></div>
            <div className="md:hidden pointer-events-none absolute top-0 bottom-0 left-0 w-6 bg-gradient-to-r from-white/95 to-transparent z-10"></div>

            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {mockData.clients[language].map((client, index) => (
                  <CarouselItem key={index} className="pl-2 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <Card className="h-full hover:shadow-lg transition-all duration-300 border-0 bg-white/90 backdrop-blur-sm group">
                      <CardContent className="p-8 flex flex-col items-center text-center">
                        <div className="mb-6 relative">
                          <img 
                            src={client.logo} 
                            alt={client.name}
                            className="h-16 w-auto object-contain mx-auto mb-4 transition-transform group-hover:scale-105"
                          />
                        </div>
                        
                        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                          {client.name}
                        </h3>
                        
                        <div className="flex items-center space-x-2 mb-3">
                          <Globe className="w-4 h-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-600">{client.country}</span>
                        </div>
                        
                        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                          {client.description}
                        </p>
                        
                        <a 
                          href={client.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors group-hover:underline"
                        >
                          <span>{language === 'en' ? 'Visit Website' : 'Visitar Sitio Web'}</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-12 bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-white hover:border-blue-300" />
              <CarouselNext className="hidden md:flex -right-12 bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-white hover:border-blue-300" />
            </Carousel>

            {/* Mobile swipe hint */}
            <div className="md:hidden mt-6 flex items-center justify-center gap-2 text-blue-600" data-testid="clients-mobile-swipe-hint">
              <ChevronLeft className="w-4 h-4 animate-[bounce_1.5s_ease-in-out_infinite] [animation-direction:alternate]" />
              <span className="text-sm font-medium">
                {language === 'en' ? 'Swipe to see more clients' : 'Desliza para ver más clientes'}
              </span>
              <ChevronRight className="w-4 h-4 animate-[bounce_1.5s_ease-in-out_infinite]" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {t.sections.servicesTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.sections.servicesSubtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mockData.services[language].map((service, index) => (
              <Card key={index} className="group h-full hover:shadow-xl transition-all duration-300 border-0 bg-white/90 backdrop-blur-sm hover:scale-105">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl mb-4 inline-block group-hover:from-blue-100 group-hover:to-indigo-100 transition-colors">
                      <service.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      {service.description}
                    </p>
                  </div>
                  
                  <div className="mt-auto">
                    <ul className="space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-2 text-xs text-slate-600">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* LATAM Processors Map Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background Map Image */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
          {/* Americas Map Background */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 1000'%3E%3Cpath d='M300 50 L350 45 L400 55 L430 75 L450 100 L460 130 L455 160 L440 180 L420 190 L400 195 L380 200 L360 210 L350 230 L345 250 L350 270 L360 290 L370 310 L375 330 L380 350 L390 370 L400 390 L410 410 L420 430 L425 450 L430 470 L435 490 L440 510 L445 530 L450 550 L455 570 L460 590 L465 610 L470 630 L472 650 L470 670 L465 690 L455 710 L445 730 L435 750 L425 770 L415 790 L405 810 L395 830 L385 850 L375 860 L365 865 L355 862 L345 855 L335 845 L325 835 L315 825 L305 815 L295 805 L285 795 L275 785 L265 775 L255 765 L245 755 L235 745 L225 735 L215 725 L205 715 L195 705 L185 695 L175 685 L165 675 L155 665 L145 655 L135 645 L125 635 L115 625 L105 615 L95 605 L85 595 L75 585 L65 575 L55 565 L50 555 L48 545 L50 535 L55 525 L65 515 L75 505 L85 495 L95 485 L105 475 L115 465 L125 455 L135 445 L145 435 L155 425 L165 415 L175 405 L185 395 L195 385 L205 375 L215 365 L225 355 L235 345 L245 335 L255 325 L265 315 L275 305 L285 295 L295 285 L305 275 L315 265 L325 255 L335 245 L345 235 L355 225 L365 215 L375 205 L385 195 L395 185 L405 175 L415 165 L425 155 L435 145 L445 135 L455 125 L465 115 L475 105 L485 95 L495 85 L505 75 L515 65 L525 55 Z' fill='white' stroke='white' stroke-width='2'/%3E%3C/svg%3E")`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          ></div>
          
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-indigo-600/20"></div>
          
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}
          ></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.sections.latamProcessorsTitle}
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              {t.sections.latamProcessorsSubtitle}
            </p>
          </div>
          
          {/* Processors Grid - Responsive */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {mockData.latamProcessors[language].map((item, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-colors duration-300 group"
              >
                {/* Large Flag Image on Top */}
                <div className="flex justify-center mb-3">
                  <img 
                    src={`https://flagcdn.com/w160/${item.flagCode}.png`}
                    srcSet={`https://flagcdn.com/w320/${item.flagCode}.png 2x`}
                    alt={`${item.country} flag - PaymentPower supports local payment processors in ${item.country}`}
                    className="w-20 h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      // Fallback to emoji if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div 
                    className="text-6xl leading-none hidden"
                    style={{ 
                      fontFamily: "'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif",
                      fontSize: '4rem'
                    }}
                  >
                    {item.flag}
                  </div>
                </div>
                
                {/* Country Name */}
                <h3 className="text-xl font-bold text-white text-center mb-4">
                  {item.country}
                </h3>
                
                {/* Processors on Bottom */}
                <div className="flex flex-wrap justify-center gap-2">
                  {item.processors.map((processor, i) => (
                    <span 
                      key={i}
                      className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium border border-white/30"
                    >
                      {processor}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Stats Card */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-white mb-2">6</div>
                <div className="text-blue-200">
                  {language === 'en' ? 'Countries Covered' : 'Países Cubiertos'}
                </div>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">12</div>
                <div className="text-blue-200">
                  {language === 'en' ? 'Local Processors' : 'Procesadores Locales'}
                </div>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">100%</div>
                <div className="text-blue-200">
                  {language === 'en' ? 'Coverage' : 'Cobertura'}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </section>

      {/* Target Audience Section */}
      <section id="audience" className="py-24 bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {t.sections.targetAudienceTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.sections.targetAudienceSubtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.targetAudience[language].map((audience, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300 border-0 bg-white/90 backdrop-blur-sm">
                <CardContent className="p-0">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                      <audience.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{audience.title}</h3>
                      <p className="text-sm text-slate-600">{audience.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    {audience.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 font-medium">
                      {language === 'en' ? 'Examples:' : 'Ejemplos:'} {audience.examples}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* LATAM Focus Badge */}
          <div className="text-center mt-16">
            <Badge className="bg-gradient-to-r from-green-100 to-blue-100 text-slate-800 border-green-200 px-6 py-3 text-base">
              🌎 {language === 'en' ? 'Specializing in Latin American Markets' : 'Especializado en Mercados Latinoamericanos'}
            </Badge>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Supporting PSPs, banks, and fintechs across Mexico, Colombia, Peru, Brazil, Argentina, and beyond with local compliance and payment methods.'
                : 'Apoyando PSPs, bancos y fintechs en México, Colombia, Perú, Brasil, Argentina y más con cumplimiento local y métodos de pago.'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {t.sections.pricingTitle}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.sections.pricingSubtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.pricingPlans[language].map((plan, index) => (
              <Card key={index} className={`p-8 relative ${
                plan.popular 
                  ? 'border-2 border-blue-500 shadow-xl bg-white' 
                  : 'border border-slate-200 bg-white/80'
              }`}>
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                    {language === 'en' ? 'Most Popular' : 'Más Popular'}
                  </Badge>
                )}
                <CardContent className="p-0">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-slate-900 mb-2">
                      {plan.price}
                      <span className="text-lg font-normal text-slate-600">
                        {language === 'en' ? '/month' : '/mes'}
                      </span>
                    </div>
                    <p className="text-slate-600">{plan.description}</p>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <p
                    className="text-xs italic text-slate-500 mb-4 flex items-start gap-1.5"
                    data-testid={`pricing-3ds-note-${index}`}
                  >
                    <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                    <span>
                      {language === 'en'
                        ? '3DS authentications billed separately'
                        : 'Autenticaciones 3DS facturadas por separado'}
                    </span>
                  </p>
                  
                  <Button 
                    className={`w-full py-3 ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white' 
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    onClick={() => setContactModalOpen(true)}
                    data-testid={`pricing-cta-${index}`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {t.sections.faqTitle}
            </h2>
            <p className="text-xl text-slate-600">
              {t.sections.faqSubtitle}
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {mockData.faqs[language].map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.sections.ctaTitle}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t.sections.ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg"
              onClick={() => setContactModalOpen(true)}
              data-testid="cta-contact-sales-btn"
            >
              {t.sections.ctaButton2}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3de5042f-38bc-4c59-be9d-105ed5dfd1b8/artifacts/mfv2x0ox_paymentpower%20azul.webp" 
                  alt="PaymentPower"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-slate-400 mb-4">
                {t.sections.footerDescription}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{mockData.footer[language].product.title}</h3>
              <ul className="space-y-2 text-slate-400">
                {mockData.footer[language].product.links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="hover:text-white transition-colors">{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{mockData.footer[language].company.title}</h3>
              <ul className="space-y-2 text-slate-400">
                {mockData.footer[language].company.links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="hover:text-white transition-colors">{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{mockData.footer[language].support.title}</h3>
              <ul className="space-y-2 text-slate-400">
                {mockData.footer[language].support.links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="hover:text-white transition-colors">{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-slate-400">
              {t.sections.footerCopyright}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              {mockData.footer[language].bottomLinks.map((link, index) => (
                <a key={index} href={link.href} className="text-slate-400 hover:text-white transition-colors">
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={contactModalOpen} 
        onClose={() => setContactModalOpen(false)} 
        language={language}
      />
    </div>
  );
};

export default LandingPage;